/**
 * useApprovalsBadgeCount — total count of items currently awaiting MY action.
 * Combines pending expenses (anything we as a user can approve/reject) with
 * pending coin transfers we must act on. Reused by Layout to drive the red
 * dot on the bottom-nav "Approvals" icon. iter106.
 *
 * Strategy: fetch once on mount + refresh on the realtime events that change
 * either pending list. No timer-based polling — the SSE bus already pings us
 * the moment a new approval lands.
 */
import { useEffect, useState } from 'react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useRealtime } from '../context/RealtimeContext';

const RELEVANT_EVENTS = [
  'expense_pending',
  'expense_approved',
  'expense_rejected',
  'expense_reversed',
  'expense_deleted',
  'coin_transfer_request',
  'coin_transfer_approved',
  'coin_transfer_rejected',
  'fund_redemption_requested',
  'fund_redemption_approved',
  'fund_redemption_rejected',
];

export default function useApprovalsBadgeCount() {
  const { user } = useAuth();
  const { onEvent } = useRealtime();
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;

    const fetchCount = async () => {
      try {
        const [exp, tx, red] = await Promise.all([
          api.get('/expenses/pending').catch(() => ({ data: [] })),
          api.get('/coin-transfers/pending').catch(() => ({ data: [] })),
          api.get('/wallet/fund-redemptions/pending').catch(() => ({ data: { rows: [] } })),
        ]);
        if (cancelled) return;
        // iter244 — stale (>30d) pending expenses don't count toward the badge
        const STALE_MS = 30 * 24 * 60 * 60 * 1000;
        const expCount = (exp.data || []).filter((e) => {
          const t = new Date(e.created_at || e.date || 0).getTime();
          return !(Number.isFinite(t) && t > 0 && Date.now() - t > STALE_MS);
        }).length;
        const txCount = (tx.data || []).filter((t) => {
          const isRequest = t.direction === 'request';
          const isFundPayer = isRequest && t.from_user_id === 'group_fund';
          if (isRequest) {
            if (isFundPayer) return true; // backend already scoped to my admin groups
            return t.from_user_id === user.id; // peer payer
          }
          return t.to_user_id === user.id; // send → recipient
        }).length;
        // Only count redemptions where I'm NOT the requester (I need to act)
        const redCount = (red.data?.rows || []).filter((r) => r.user_id !== user.id).length;
        setCount(expCount + txCount + redCount);
      } catch {
        if (!cancelled) setCount(0);
      }
    };

    fetchCount();
    const unsubscribe = onEvent(RELEVANT_EVENTS, fetchCount);
    return () => { cancelled = true; if (unsubscribe) unsubscribe(); };
  }, [user?.id, onEvent]);

  return count;
}
